"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223731" className="hidden md:flex gap-8">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-none" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-none" />
        <div className="h-4 w-16 bg-muted animate-pulse rounded-none" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent data-[active=true]:text-foreground hover:text-foreground p-0">
              <Link to={item.href || "#"} className="group relative font-default text-[15px] font-medium uppercase tracking-widest text-foreground transition-colors">
                {item.label}
                <span className="absolute left-0 bottom-0 h-[1px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-gradient-to-b from-accent/30 via-background to-background backdrop-blur-2xl border-b border-border/10 md:hidden shadow-xl">
      <nav className="flex flex-col p-6 gap-6">
        {loading ? <div className="flex flex-col gap-6">
            <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
            <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="group relative w-fit font-default text-lg font-medium uppercase tracking-widest text-foreground" onClick={() => setIsOpen(false)}>
              {item.label}
              <span className="absolute left-0 bottom-0 h-[1px] w-full origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100" />
            </Link>)}
      </nav>
    </div>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 text-foreground ${scrolled ? "bg-background/80 backdrop-blur-xl border-b border-transparent shadow-sm" : "bg-background border-b border-transparent shadow-none"}`}>
      <div className="absolute inset-0 w-full h-full overflow-hidden -z-10">
        <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-50">
          <source src="/img/hero_water_ripples_skin_texture.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="container relative mx-auto px-6 h-24 flex items-center justify-between">
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://stable-sturgeon.10web.cloud/wp-content/uploads/2026/04/BC_logo_black.png" alt="BODYCONCEPT Allendorf by Linda" className="h-20 object-contain brightness-0 !w-[320px]" />
          </Link>
        </div>

        <MenuProvider menu_id="45">
          <DesktopMenu />
          
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/30 rounded-full transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Menü umschalten">
            {mobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>

          <MobileMenu isOpen={mobileMenuOpen} setIsOpen={setMobileMenuOpen} />
        </MenuProvider>
      </div>
    </section>;
}